import React, { createContext, useContext } from 'react';
import { TriangleAlert } from 'lucide-react';
import { Tooltip } from '@cotera/client/app/components';
import type { ToolInputRefIssue } from './node/edit-strategies/tool-input-ref-issues';
import type { WorkflowRefIssuesByNodeId } from '../hooks/use-workflow-ref-issues';

const EMPTY: WorkflowRefIssuesByNodeId = new Map();

/**
 * Stale refs for the whole graph, computed once at the canvas and read per node.
 *
 * Through context rather than through each node's `data`, because the answer
 * does not come from the graph: it depends on a dataset's columns and an
 * agent's output shape, both of which change without the graph changing at all.
 * Putting it in `data` would mean rebuilding the flow nodes whenever either
 * moved, and re-running the layout with them.
 */
const WorkflowRefIssuesContext =
  createContext<WorkflowRefIssuesByNodeId>(EMPTY);

export const WorkflowRefIssuesProvider: React.FC<{
  readonly issues: WorkflowRefIssuesByNodeId;
  readonly children: React.ReactNode;
}> = ({ issues, children }) => (
  <WorkflowRefIssuesContext.Provider value={issues}>
    {children}
  </WorkflowRefIssuesContext.Provider>
);

/** The stale refs on one node, empty when it has none (or nothing is known). */
export function useNodeRefIssues(nodeId: string): readonly ToolInputRefIssue[] {
  return useContext(WorkflowRefIssuesContext).get(nodeId) ?? [];
}

/**
 * The mark a node carries when something it reads no longer exists.
 *
 * Says which values are broken rather than only that something is, so the
 * tooltip is worth stopping for: on a graph where three nodes went stale
 * together, "this one lost `time.actual_time`" is what tells them apart. The
 * same messages the drawer shows, from the same check.
 */
export const WorkflowNodeRefIssueMark: React.FC<{
  readonly issues: readonly ToolInputRefIssue[];
}> = ({ issues }) => {
  if (issues.length === 0) {
    return null;
  }
  return (
    <Tooltip
      side="top"
      tooltipContent={
        <span className="flex flex-col gap-1">
          {issues.map((issue) => (
            <span key={issue.path}>{issue.message}</span>
          ))}
        </span>
      }
    >
      <span
        // Same warning colour as the notice in the drawer, so the mark and the
        // message it stands for read as one thing.
        className="flex size-5 shrink-0 cursor-pointer items-center justify-center rounded-full border border-yellow-300 bg-yellow-50 text-yellow-800"
        role="alert"
        aria-label={`${issues.length} value${
          issues.length === 1 ? '' : 's'
        } to fix`}
      >
        <TriangleAlert className="size-3" />
      </span>
    </Tooltip>
  );
};
